import React from "react";

interface EditCellProps {
    /** The field: the shared Input, AddressSearch or a react-select, rendered without its own label chrome. */
    children: React.ReactNode;
    /** The value differs from the last saved one — gold "unsaved" ring, same as Input's isChanged. */
    isChanged?: boolean;
    /** Esc puts the cell back to the value it had when it got focus (the page restores it). */
    onRevert?: () => void;
    /** Hover help — what the column means. */
    title?: string;
    minWidth?: number;
    /** "end" right-aligns the value (money, counts). */
    align?: "start" | "end";
    className?: string;
}

const FOCUSABLE = "input:not([type=hidden]):not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex='-1'])";

/**
 * Focuses the same column in the next (or previous) table row, so Enter in
 * a cell moves down the sheet. Returns false when there is no such cell.
 */
const focusSiblingRow = (cell: HTMLElement, direction: 1 | -1): boolean => {
    const td = cell.closest("td");
    const tr = td?.parentElement as HTMLTableRowElement | null;
    if (!td || !tr) return false;
    let row = (direction === 1 ? tr.nextElementSibling : tr.previousElementSibling) as HTMLTableRowElement | null;
    while (row) {
        const target = row.cells[td.cellIndex]?.querySelector<HTMLElement>(FOCUSABLE);
        if (target) {
            target.focus();
            return true;
        }
        row = (direction === 1 ? row.nextElementSibling : row.previousElementSibling) as HTMLTableRowElement | null;
    }
    return false;
};

/**
 * One editable cell of a work-surface table (movements, pricing lines,
 * draft invoice lines). At rest it reads as text; hover shows a faint
 * border, focus the full field. Enter moves to the same column in the next
 * row (Shift+Enter: previous row), Esc reverts the cell and leaves it.
 * Clicks stay inside the cell so a row's own click handler doesn't fire.
 */
const EditCell: React.FC<EditCellProps> = ({ children, isChanged, onRevert, title, minWidth, align = "start", className }) => {
    const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
        // A picker that used the key itself (react-select choosing an option) has already handled it.
        if (event.defaultPrevented) return;
        const target = event.target as HTMLElement;
        if (event.key === "Enter" && target.tagName !== "TEXTAREA") {
            if (focusSiblingRow(event.currentTarget, event.shiftKey ? -1 : 1)) event.preventDefault();
        } else if (event.key === "Escape" && onRevert) {
            event.preventDefault();
            onRevert();
            target.blur();
        }
    };

    return (
        <span
            className={[
                "ops-cell",
                "crx-cell",
                isChanged ? "crx-cell--changed" : "",
                align === "end" ? "crx-cell--end" : "",
                className ?? "",
            ].filter(Boolean).join(" ")}
            title={title}
            style={minWidth ? { minWidth } : undefined}
            onClick={(event) => event.stopPropagation()}
            onKeyDown={handleKeyDown}
        >
            {children}
        </span>
    );
};

export default EditCell;
